import { useAutofillCurrency } from 'hooks/useAutofillCurrency';
import { useProviderCurrencyIds } from 'hooks/useProviderCurrencyIds';
import { RaRecord, required, useRecordContext } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { TextField } from 'shared/mui/TextField/TextField';
import { Typography } from 'shared/mui/Typography';
import { AutocompleteInput, AutocompleteMultiselectInput } from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { Commission } from 'types/embedded/commission';
import { cleanEmpty } from 'utils/cleanEmpty';

import { BoxStyled } from './CommissionsBaseForm.styled';
import { CommissionsNameInput } from '../CommissionsNameInput';

export type CommissionsBaseFormValues = {
  name: string;
  merchant: string;
  provider: string | null;
  currency: string;
  transactionTypes: string[];
};

export const CommissionsBaseForm = () => {
  const record = useRecordContext<Commission>();

  const { formState, watch, setValue } = useFormContext<CommissionsBaseFormValues>();
  const merchantWatched = watch('merchant');
  const provider = watch('provider');

  const currencyIds = useProviderCurrencyIds(provider);
  useAutofillCurrency(currencyIds);

  return (
    <BoxStyled>
      <Typography fontWeight={500} sx={{ gridColumn: ' 1 / span 4' }} variant="body1">
        Основные настройки
      </Typography>
      <CommissionsNameInput
        fullWidth
        label="Название"
        size="medium"
        source="name"
        sx={{ gridColumn: ' 1 / span 4' }}
        validate={required()}
        variant="outlined"
      />
      <ReferenceInput
        filter={{ archive: false }}
        perPage={100}
        reference="merchants"
        source="merchant"
      >
        <AutocompleteInput
          filterToQuery={(searchText: string) => ({ name: searchText })}
          fullWidth
          isRequired={true}
          label="Мерчант"
          onChange={() => {
            setValue('provider', null);
            setValue('currency', '');
          }}
          optionText="name"
          readOnly={!!record?.parent}
          size="small"
          variant="outlined"
        />
      </ReferenceInput>
      <ReferenceInput
        filter={{ merchantAccounts: { merchants: merchantWatched } }}
        perPage={100}
        reference="providers"
        source="provider"
      >
        <AutocompleteInput
          disabled={!merchantWatched}
          filterToQuery={(searchText: string) => ({ name: searchText })}
          fullWidth
          isRequired
          label="Провайдер"
          onChange={() => {
            setValue('currency', '');
          }}
          optionText="name"
          readOnly={!!record?.parent}
          size="small"
          source="provider"
          variant="outlined"
        />
      </ReferenceInput>
      <ReferenceArrayInput
        perPage={100}
        reference="transaction_types/commission/list"
        source="transactionTypes"
      >
        <AutocompleteMultiselectInput
          fullWidth
          isRequired
          label="Типы транзакций"
          name="transactionTypes"
          optionText="name"
          readOnly={!!record?.parent}
          size="small"
          sx={{
            '.MuiTextField-root': {
              marginTop: 0,
            },
          }}
        />
      </ReferenceArrayInput>
      <ReferenceInput
        filter={cleanEmpty({ id: currencyIds.length ? currencyIds : undefined, active: true })}
        perPage={100}
        reference="currencies"
        sort={{ field: 'id', order: 'ASC' }}
        source="currency"
      >
        <AutocompleteInput
          disabled={!provider}
          filterToQuery={(searchText: string) => ({ currency: searchText })}
          fullWidth
          isRequired={true}
          name="currency"
          optionText={(record: RaRecord) => `${record?.alphaCode}, ${record?.name}`}
          readOnly={!!record?.parent}
          renderInput={(params) => (
            <TextField
              {...params}
              InputLabelProps={{ shrink: true }}
              error={!!formState.errors?.currency}
              helperText={(formState.errors?.currency?.message as string) || ' '}
              label="Валюта"
              required={true}
              size="small"
              variant="outlined"
            />
          )}
          variant="outlined"
        />
      </ReferenceInput>
    </BoxStyled>
  );
};
